import { createApp } from 'vue';
import { createPinia } from 'pinia';
import Copilot from '@deputy/copilot2';
import App from './App.vue';
import router from './router';
import { datadogRum } from '@datadog/browser-rum';
import { isDev } from './lib/util/isDev';
import { useKioskConfigStore } from './stores/kioskConfig';
import VueVirtualScroller from 'vue-virtual-scroller';

declare let __COMMIT_HASH__: string;

const app = createApp(App);
app.use(createPinia());
app.use(router);
app.use(Copilot);
app.use(VueVirtualScroller);

Copilot.loadIcon();

if (!isDev()) {
  const version = `v${__COMMIT_HASH__}`;
  datadogRum.init({
    applicationId: 'a40efeff-3bcb-4655-9485-ca7468025a14',
    clientToken: 'pub5b8bcd60a3f6695d6a6755a3c57cc94f',
    site: 'datadoghq.com',
    service: 'web-time-clock',
    trackViewsManually: true,
    env: window.DP_ENVIRONMENT === 'dev' ? 'local' : window.DP_ENVIRONMENT,
    version,
    sampleRate: 100,
    sessionReplaySampleRate: 0,
    trackInteractions: true,
    defaultPrivacyLevel: 'mask',
    allowedTracingOrigins: [
      /https:\/\/.*\.deputy\.com/,
      /https:\/\/.*\.deputec\.com/,
      /https:\/\/.*\.dpty\.io/,
    ],
  });

  datadogRum.startSessionReplayRecording();

  document.body.setAttribute('data-version', version);

  useKioskConfigStore()
    .load()
    .then(() => setDatadogGlobalContext());
}

app.mount('#app');

function setDatadogGlobalContext() {
  const config = useKioskConfigStore();
  const appConfig = config.data.KioskAppConfigs || {};
  const installation = window.__APP__.analyticsContext.installation;

  const context: { [key: string]: string | boolean | number } = {
    'service_context.metadata.tenant.hostname': installation.hostname,
    'service_context.metadata.tenant.tenant_id': installation.ID,
    'service_context.metadata.kiosk.kiosk_id': config.data.KioskId,
    'service_context.metadata.kiosk.kiosk_name': config.data.KioskName,
    'service_context.metadata.kiosk.kiosk_location_timezone':
      config.data.KioskCompanyTimezone,
    'service_context.metadata.kiosk.kiosk_location_name':
      config.data.KioskCompanyName,
    'service_context.metadata.kiosk.config.photo_clockin': appConfig[
      'ALLOW_KIOSK_PHOTO_CLOCKIN'
    ]
      ? 'on'
      : 'off',
    'service_context.metadata.kiosk.config.has_custom_fields': appConfig[
      'HAS_CLOCKIN_CUSTOM_FIELDS'
    ]
      ? 'on'
      : 'off',
    'service_context.metadata.kiosk.config.multi_location': appConfig[
      'ENABLE_MULTI_LOCATIONS'
    ]
      ? 'on'
      : 'off',
    'service_context.metadata.kiosk.config.require_photo': appConfig[
      'REQUIRE_KIOSK_PHOTO_BUMP_SHIFT'
    ]
      ? 'on'
      : 'off',
    'service_context.metadata.user.screen': `${screen.width}x${screen.height}`,
    'service_context.metadata.kiosk.location_id': config.data.KioskCompanyId,
  };

  Object.keys(context).forEach((key) =>
    datadogRum.setGlobalContextProperty(key, context[key])
  );
}
